import { useEffect, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Pagination from "@app/components/pagination";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES, MAX_LEN, PathnameUrl, RechargeState } from "@app/constants";
import RechargeService from "@app/services/http/recharge.service";
import { Recharge, RechargeKind, RechargeSummary } from "@app/types";
import { formatDate, formatMoneyVN } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { parsePaymentLink, rechargeTimeLeft } from "@app/components/recharge-qr-modal";
import { IcCart, IcQr, IcReceipt, IcRefresh, IcSearch, IcWallet } from "@app/components/icons";
import { CopyButton, LoadingBlock, StatusPill, btn, btnGhost, btnPrimary, btnSm, select } from "@app/components/ui";

const ADMIN_BANK = "cập nhật từ ADMIN";
const PER_PAGE = 10;

/** Số tiền có dấu: amount, dữ liệu cũ dùng money */
export const signedAmount = (r: Recharge) => (typeof r.amount === "number" ? r.amount : r.money);

const isAdmin = (r: Recharge) => r.type === "admin" || r.bank === ADMIN_BANK;
const isDeposit = (r: Recharge) => !r.type || r.type === "recharge" || isAdmin(r);

/** Loại theo bản thiết kế: Nạp tiền (nạp + admin cộng), Hoàn tiền (*_refund), còn lại là Trừ tiền */
const kindOf = (r: Recharge): Exclude<RechargeKind, ""> =>
  /_refund$/.test(r.type || "") ? "refund" : isDeposit(r) && signedAmount(r) >= 0 ? "top" : "spend";

const KIND: Record<Exclude<RechargeKind, "">, [string, string, typeof IcWallet]> = {
  top: ["Nạp tiền", "bg-ok-soft text-ok", IcWallet],
  spend: ["Trừ tiền", "bg-bad-soft text-bad", IcCart],
  refund: ["Hoàn tiền", "bg-brand-soft text-brand", IcRefresh],
};
const AMOUNT_COLOR = { top: "text-ok", spend: "text-bad", refund: "text-brand" };

const TABS: [RechargeKind, string][] = [
  ["", "Tất cả"],
  ["top", "Nạp tiền"],
  ["spend", "Trừ tiền"],
  ["refund", "Hoàn tiền"],
];

const th = "whitespace-nowrap bg-surface-2 px-2 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[.05em] text-faint first:pl-5 last:pr-5";
const td = "border-t border-line px-2 py-3 align-middle first:pl-5 last:pr-5";

const mmss = (ms: number) => {
  const s = Math.floor(ms / 1000);

  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

interface BalanceHistoryProps {
  /** Tăng lên để tải lại (vừa tạo / huỷ đơn nạp, nhận socket nạp tiền) */
  reload: number;
  balance: number;
  summary?: RechargeSummary;
  onOpenQr: (recharge: Recharge) => void;
}

/** Biến động số dư theo bản thiết kế: GET /recharges (model recharge lưu mọi lần nạp / trừ / hoàn tiền) */
function BalanceHistory({ reload, balance, summary, onOpenQr }: BalanceHistoryProps) {
  const t = useT();
  const navigate = useNavigate();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<Recharge[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [kind, setKind] = useState<RechargeKind>("");
  // lọc theo trạng thái (cột Trạng thái): "" = tất cả
  const [status, setStatus] = useState<"" | RechargeState>("");
  const [q, setQ] = useState("");
  const [keyword, setKeyword] = useState("");
  const [counts, setCounts] = useState<Partial<Record<RechargeKind, number>>>({});
  const [, setTick] = useState(0);

  const { subscribeUntilDestroy } = useObservable();

  // tìm kiếm chờ 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setKeyword(q.trim());
      setPage(DEFAULT_PAGE);
    }, 300);

    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    subscribeUntilDestroy(
      RechargeService.getRecharges(page, { kind, keyword, status: status === "" ? undefined : status }),
      (res) => {
        setRows(res?.data || []);
        setTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
        setCount(res?.count || 0);
        setLoaded(true);
      }
    );
  }, [page, kind, keyword, status, reload]);

  // số trên các tab lọc (không theo ô tìm kiếm)
  useEffect(() => {
    TABS.forEach(([key]) =>
      subscribeUntilDestroy(RechargeService.getRecharges(DEFAULT_PAGE, { kind: key }), (res) =>
        setCounts((c) => (c[key] === res?.count ? c : { ...c, [key]: res?.count || 0 }))
      )
    );
  }, [reload]);

  // đồng hồ "Còn mm:ss" của lệnh nạp đang chờ
  const hasPending = rows.some((r) => r.is_succeeded === RechargeState.PENDING && rechargeTimeLeft(r) > 0);

  useEffect(() => {
    if (!hasPending) return;

    const timer = setInterval(() => setTick((n) => n + 1), 1000);

    return () => clearInterval(timer);
  }, [hasPending]);

  const viewOrder = (r: Recharge) =>
    navigate(`${PathnameUrl.ORDER_MANAGEMENT}?type=${r.service}&q=${encodeURIComponent(r.ref_id || "")}`);

  const from = (page - 1) * PER_PAGE;

  return (
    <section id="balance-history" className="scroll-mt-20 overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 max-[760px]:px-3.5">
        <div>
          <h2 className="flex items-center gap-2 text-[17px] font-bold">
            {t("Biến động số dư")}
            {counts[""] !== undefined && (
              <span className="rounded-full bg-brand-soft px-[9px] py-px text-xs font-semibold tabular-nums text-brand">{counts[""]}</span>
            )}
          </h2>
          <p className="m-0 mt-0.5 text-[13px] text-muted">
            {t("Mọi khoản nạp, trừ và hoàn tiền theo thứ tự thời gian, kèm số dư sau mỗi giao dịch.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 tabular-nums max-[560px]:w-full">
          {(
            [
              ["Nạp tháng này", summary ? `+${formatMoneyVN(summary.month_in)}đ` : "—", "text-ok"],
              ["Chi tháng này", summary ? `−${formatMoneyVN(summary.month_out)}đ` : "—", "text-bad"],
              ["Số dư hiện tại", `${formatMoneyVN(balance)}đ`, ""],
            ] as [string, string, string][]
          ).map(([label, value, color]) => (
            <div key={label} className="min-w-0 rounded-[10px] bg-surface-2 px-3 py-1.5 leading-tight max-[560px]:flex-1">
              <span className="block text-[11.5px] text-muted">{t(label)}</span>
              <b className={clsx("whitespace-nowrap text-[14.5px]", color)}>{value}</b>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-3 max-[760px]:px-3.5">
        <label className="flex h-9 min-w-[220px] flex-[1_1_260px] items-center gap-2 rounded-[10px] border border-line bg-surface px-3 text-muted focus-within:border-brand">
          <IcSearch className="h-[18px] w-[18px] flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Tìm theo nội dung, mã đơn, số tiền…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
          />
        </label>
        <div className="rtabs inline-flex flex-wrap gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
          {TABS.map(([key, label]) => (
            <button
              key={key || "all"}
              type="button"
              aria-pressed={kind === key}
              onClick={() => {
                setKind(key);
                setPage(DEFAULT_PAGE);
              }}
              className={clsx(
                "inline-flex items-baseline gap-1.5 rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                kind === key ? "bg-surface text-fg shadow-card" : "text-muted"
              )}
            >
              {t(label)}
              {counts[key] !== undefined && <em className="text-[11.5px] not-italic tabular-nums text-faint">{counts[key]}</em>}
            </button>
          ))}
        </div>
        <select
          className={clsx(select, "!h-9 max-[560px]:flex-1")}
          aria-label={t("Lọc theo trạng thái")}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value === "" ? "" : (Number(e.target.value) as RechargeState));
            setPage(DEFAULT_PAGE);
          }}
        >
          <option value="">{t("Tất cả trạng thái")}</option>
          <option value={RechargeState.SUCCESSFULLY}>{t("Thành công")}</option>
          <option value={RechargeState.PENDING}>{t("Chờ thanh toán")}</option>
          <option value={RechargeState.CANCELLED}>{t("Đã huỷ")}</option>
          <option value={RechargeState.TIMEOUT}>{t("Hết hạn")}</option>
        </select>
      </div>

      {!loaded ? (
        <LoadingBlock />
      ) : !rows.length ? (
        <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
            <IcWallet className="h-[26px] w-[26px]" />
          </div>
          <h3 className="text-base font-bold">{t("Không tìm thấy giao dịch nào")}</h3>
          <p className="m-0 text-muted">{t("Thử từ khoá khác hoặc chọn bộ lọc \"Tất cả\".")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* otbl lgtbl: dưới 1100px mỗi giao dịch thành 1 thẻ như bản thiết kế (styles/_responsive.scss) */}
          <table className="otbl lgtbl w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className={th}>{t("Thời điểm")}</th>
                <th className={th}>{t("Loại")}</th>
                <th className={th}>{t("Nội dung")}</th>
                <th className={clsx(th, "!text-right")}>{t("Số tiền")}</th>
                <th className={clsx(th, "!text-right")}>{t("Số dư sau")}</th>
                <th className={th}>{t("Trạng thái")}</th>
                <th className={th}>
                  <span className="sr-only">{t("Thao tác")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const k = kindOf(r);
                const [kindLabel, kindClass, KindIcon] = KIND[k];
                const amount = Math.abs(signedAmount(r));
                const deposit = k === "top";
                const pendingLeft = r.is_succeeded === RechargeState.PENDING ? rechargeTimeLeft(r) : 0;
                const pending = pendingLeft > 0;
                // giao dịch có làm đổi số dư hay chưa (lệnh nạp chờ / huỷ / hết hạn thì không)
                const live = r.is_succeeded === RechargeState.SUCCESSFULLY;
                const bank = deposit && !isAdmin(r) ? parsePaymentLink(r.link_payment) : undefined;

                let status: [string, "ok" | "warn" | "bad" | "muted"] = k === "refund" ? ["Đã hoàn", "ok"] : ["Thành công", "ok"];

                if (deposit && !live) {
                  status = pending
                    ? ["Chờ thanh toán", "warn"]
                    : r.is_succeeded === RechargeState.CANCELLED
                    ? ["Đã huỷ", "bad"]
                    : ["Hết hạn", "muted"];
                }

                return (
                  <tr key={r._id} className={clsx(deposit && !live && !pending && "text-muted")}>
                    <td data-label={t("Thời điểm")} className={clsx(td, "lg-t whitespace-nowrap tabular-nums text-muted")}>{formatDate(r.createdAt, "DD/MM/YYYY HH:mm")}</td>
                    <td data-label={t("Loại")} className={td}>
                      <span className={clsx("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold", kindClass)}>
                        <KindIcon className="h-[13px] w-[13px]" />
                        {t(kindLabel)}
                      </span>
                    </td>
                    <td className={clsx(td, "o-m lg-c max-w-[380px]")}>
                      {deposit ? (
                        <>
                          <b className="block font-semibold text-fg">
                            {isAdmin(r) ? r.description || t("Admin cộng tiền") : t("Nạp tiền chuyển khoản")}
                          </b>
                          {!isAdmin(r) && (
                            <span className="flex items-center gap-0.5 text-[11.5px] text-muted">
                              <span className="font-mono">{r.content}</span>
                              <CopyButton value={r.content} label="Sao chép nội dung" />
                            </span>
                          )}
                          <small className="block truncate text-xs text-faint">
                            {isAdmin(r)
                              ? t("Cộng trực tiếp vào số dư")
                              : [bank?.bankName || r.bank, bank?.bankNumber].filter(Boolean).join(" · ")}
                          </small>
                        </>
                      ) : (
                        <>
                          <b className="block font-semibold text-fg">{r.description || r.content}</b>
                          {!!r.ref_id && (
                            <span className="flex items-center gap-0.5 text-[11.5px] text-muted">
                              <span className="text-faint">{t("Mã đơn")}</span>{" "}
                              <span className="font-mono" title={r.ref_id}>
                                {r.ref_id.length > 10 ? `${r.ref_id.slice(0, 6)}…` : r.ref_id}
                              </span>
                              <CopyButton value={r.ref_id} label="Sao chép mã đơn" />
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td data-label={t("Số tiền")} className={clsx(td, "r text-right")}>
                      <b
                        className={clsx(
                          "whitespace-nowrap text-sm tabular-nums",
                          live ? AMOUNT_COLOR[k] : "text-faint line-through"
                        )}
                      >
                        {k === "spend" ? "−" : "+"}
                        {formatMoneyVN(amount)}đ
                      </b>
                    </td>
                    <td data-label={t("Số dư sau")} className={clsx(td, "r whitespace-nowrap text-right tabular-nums")}>
                      {live && typeof r.balance_after === "number" ? (
                        <b>{formatMoneyVN(r.balance_after)}đ</b>
                      ) : live ? (
                        <span className="text-faint">—</span>
                      ) : (
                        <span className="text-faint">{t("Không đổi")}</span>
                      )}
                    </td>
                    <td data-label={t("Trạng thái")} className={td}>
                      <span className="flex flex-col items-start gap-0.5">
                        <StatusPill tone={status[1]}>{t(status[0])}</StatusPill>
                        {pending && (
                          <small className="text-xs tabular-nums text-muted">
                            {t("Còn")} {mmss(pendingLeft)}
                          </small>
                        )}
                      </span>
                    </td>
                    <td className={clsx(td, "o-a text-right")}>
                      {pending && (
                        <button type="button" className={clsx(btn, btnPrimary, btnSm)} onClick={() => onOpenQr(r)}>
                          <IcQr className="h-4 w-4" />
                          {t("Mở mã QR")}
                        </button>
                      )}
                      {!!r.service && !!r.ref_id && (
                        <button type="button" className={clsx(btn, btnGhost, btnSm)} onClick={() => viewOrder(r)}>
                          <IcReceipt className="h-4 w-4" />
                          {t("Xem đơn")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loaded && !!rows.length && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-[13px] text-muted">
          <span className="tabular-nums">
            {totalPages > 1
              ? `${t("Hiển thị")} ${from + 1}–${from + rows.length} ${t("trong")} ${count} ${t("giao dịch")}`
              : `${t("Hiển thị")} ${count} ${t("giao dịch")}`}
          </span>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      )}
    </section>
  );
}

export default BalanceHistory;
